import { CircularProgress, IconButton, Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Button, FormControlLabel, Checkbox, TextField, Box, Switch } from '@material-ui/core';
import moment from 'moment'
import { makeStyles } from '@material-ui/styles';
import React, { FC } from 'react'
import Table from '../../components/Table';
import { SubscriptionSimple } from '../../types';
import colors from '../../colors';
import useSubscriptionStore from '../../stores/subscription';
import { useParams, useLocation } from 'react-router-dom';
import { MoreVertical } from 'react-feather';
import useMembershipsStore from '../../stores/memberships';
import { toast } from 'react-toastify';
import useAuthStore from '../../stores/auth';
import stitchAxios from '../../utils/axios';

interface Props { }

const MembershipSubscriptionsTable: FC<Props> = () => {
  const [loading, setLoading] = React.useState<boolean>(true)
  const [subscriptions, setSubscriptions] = React.useState<SubscriptionSimple[]>([])
  const [actionAnchorPos, setActionAnchorPos] = React.useState<{ top: number, left: number } | null>(null)
  const [selectedSub, setSelectedSub] = React.useState<SubscriptionSimple | null>(null)
  const [confirmOpen, setConfirmOpen] = React.useState(false)
  const [notifyUser, setNotifyUser] = React.useState(true)
  const [removing, setRemoving] = React.useState(false)
  const [reloadKey, setReloadKey] = React.useState(0)
  const [addModalOpen, setAddModalOpen] = React.useState(false)
  const [employeeSubmitting, setEmployeeSubmitting] = React.useState(false)
  const [employeeForm, setEmployeeForm] = React.useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
  })
  const [togglingAutoRenew, setTogglingAutoRenew] = React.useState<Record<string, boolean>>({})
  const scrollPositionRef = React.useRef<number>(0)
  const { getSubscriptionsForMembership, updateAutoRenew } = useSubscriptionStore()
  const { memberships } = useMembershipsStore()
  const { user } = useAuthStore()
  const location = useLocation()
  const styles = useStyles()
  const { id } = useParams()
  const membership = id ? memberships[id] : undefined
  const isEmployeeBenefit = membership?.membershipType === 'employee_benefit'

  React.useEffect(() => {
    (async () => {
      if (!id) {
        return
      }
      const response = await getSubscriptionsForMembership(id)
      setSubscriptions(response.data)
      setLoading(false)
    })()
  }, [getSubscriptionsForMembership, id, reloadKey, location.state])

  const handleActionClick = (sub: SubscriptionSimple) => (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setSelectedSub(sub)
    setActionAnchorPos({ top: event.clientY, left: event.clientX })
  }

  const closeMenu = () => {
    setActionAnchorPos(null)
  }

  const openRemoveDialog = () => {
    setConfirmOpen(true)
    closeMenu()
  }

  const closeRemoveDialog = () => {
    if (removing) return
    setConfirmOpen(false)
    setSelectedSub(null)
    setNotifyUser(true)
  }

  const handleEmployeeFieldChange = (field: keyof typeof employeeForm) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmployeeForm((prev) => ({
      ...prev,
      [field]: event.target.value,
    }))
  }

  const closeAddModal = () => {
    if (employeeSubmitting) return
    setAddModalOpen(false)
    setEmployeeForm({
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
    })
  }

  const handleAddEmployee = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!membership) return
    const organizationSlug = membership.organizationSlug || user?.organization?.slug
    if (!organizationSlug) {
      toast.error('Organization is missing a slug; cannot add employee.')
      return
    }
    const payload = {
      organization_slug: organizationSlug,
      membership_uuid: membership.uuid,
      patron: {
        first_name: employeeForm.firstName,
        last_name: employeeForm.lastName,
        phone: employeeForm.phone,
        email: employeeForm.email,
      },
    }
    try {
      setEmployeeSubmitting(true)
      await stitchAxios.post('/employee_memberships', payload)
      toast.success('Employee membership added')
      setReloadKey((k) => k + 1)
      closeAddModal()
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to add employee membership'
      toast.error(errorMessage)
    } finally {
      setEmployeeSubmitting(false)
    }
  }

  const handleRemove = async () => {
    if (!selectedSub || !membership) return
    const organizationSlug = membership.organizationSlug || user?.organization?.slug
    if (!organizationSlug) {
      toast.error('Organization is missing a slug; cannot remove employee.')
      return
    }

    const payload = {
      organization_slug: organizationSlug,
      membership_uuid: membership.uuid,
      patron_email: selectedSub.patron.email,
      notify: notifyUser,
    }

    try {
      setRemoving(true)
      await stitchAxios.delete('/employee_memberships/remove', { data: payload })
      toast.success('Employee removed')
      setReloadKey((k) => k + 1)
      closeRemoveDialog()
    } catch (error) {
      toast.error('Failed to remove employee')
    } finally {
      setRemoving(false)
    }
  }

  const handleAutoRenewToggle = (sub: SubscriptionSimple) => async (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation()

    const newStatus = event.target.checked ? 'on' : 'off'
    const subId = sub.id

    // Save scroll position before state update
    scrollPositionRef.current = window.scrollY

    try {
      setTogglingAutoRenew(prev => ({ ...prev, [subId]: true }))

      const response = await updateAutoRenew(subId, newStatus, false)

      // Update local state with the response from the server
      setSubscriptions(prev =>
        prev.map(s =>
          s.id === subId
            ? { ...s, autoRenew: response.subscription.autoRenew, status: response.subscription.status }
            : s
        )
      )

      toast.success(`Auto-renewal turned ${newStatus}`)
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || `Failed to update auto-renewal`
      toast.error(errorMessage)
      // Revert the optimistic update on error
      setSubscriptions(prev =>
        prev.map(s =>
          s.id === subId
            ? { ...s, autoRenew: sub.autoRenew }
            : s
        )
      )
    } finally {
      setTogglingAutoRenew(prev => ({ ...prev, [subId]: false }))
    }
  }

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <CircularProgress />
      </div>
    )
  }
  return (
    <div className={styles.container}>
      <Table<SubscriptionSimple>
        title={'Subscriptions'}
        rows={subscriptions}
        loading={loading}
        defaultFiltersOn
        defaultSelectedFilters={['Status-Active']}
        rightActions={isEmployeeBenefit ? (
          <Button variant="contained" color="primary" onClick={() => setAddModalOpen(true)}>
            Add Employee
          </Button>
        ) : null}
        filters={[
          {
            title: 'Status',
            matchingFunction: (s) => s.status
          },
          {
            title: 'Billing Interval',
            matchingFunction: (s) => s.interval
          },
          {
            title: 'Price',
            matchingFunction: (s) => s.price
          },
          {
            title: 'Auto Renew',
            matchingFunction: (s) => s.autoRenew === true ? 'On' : 'Off',
          },
        ]}
        columns={[{
          label: 'First Name',
          cellFill: (sub: SubscriptionSimple) => sub.patron.firstName,
          searchable: true,
          searchFunction: (s) => String(s.patron.firstName),
          sortable: true,
          csv: true,
          sortValueGetter: (s) => String(s.patron.firstName),
        },
        {
          label: 'Last Name',
          cellFill: (sub: SubscriptionSimple) => sub.patron.lastName,
          searchable: true,
          searchFunction: (s) => String(s.patron.lastName),
          csv: true,
          sortValueGetter: (s) => String(s.patron.lastName),
        },
        {
          label: 'Email',
          cellFill: (sub: SubscriptionSimple) => sub.patron.email,
          searchable: true,
          searchFunction: (s) => String(s.patron.email),
          hide: true,
          csv: true,
          sortValueGetter: (s) => String(s.patron.email),
        },
        {
          label: 'Membership',
          cellFill: (sub: SubscriptionSimple) => sub.membershipTitle,
          csv: true,
          sortValueGetter: (s) => String(s.membershipTitle),
        },
        {
          label: 'Price',
          cellFill: (sub: SubscriptionSimple) => sub.price,
          sortValueGetter: (s) => Number(String(s.price).replace('$', '').replace(',', '')),
          csv: true,
        },
        {
          label: 'Cycle Length',
          cellFill: (sub: SubscriptionSimple) => sub.interval,
          sortValueGetter: (s) => {
            console.log('Cycle Length sort - interval value:', s.interval, 'type:', typeof s.interval);
            return String(s.interval);
          },
          csv: true,
        },
        {
          label: 'Start Date',
          cellFill: (sub: SubscriptionSimple) => moment(sub.startDate).format('M/D/YYYY'),
          sortValueGetter: (s) => new Date(s.startDate),
          csv: true,
        },
        {
          label: 'Current Period End',
          cellFill: (sub: SubscriptionSimple) => moment(sub.currentCycleEnd).format('M/D/YYYY'),
          sortValueGetter: (s) => new Date(s.currentCycleEnd),
          csv: true,
        },
        {
          label: 'Status',
          cellFill: 'status',
          sortValueGetter: (s) => String(s.status),
          csv: true,
        },
        {
          label: 'Auto Renew',
          cellFill: (sub: SubscriptionSimple) => {
            const statusLower = sub.status?.toLowerCase() || ''

            // Check if subscription is in a state where auto-renewal can be toggled
            const isActive = statusLower === 'active'
            const isPendingCancel = statusLower.includes('pending') && statusLower.includes('cancel')

            // Can only toggle if active or pending_cancel
            const canToggle = isActive || isPendingCancel

            const isOn = sub.autoRenew === true

            return (
              <Switch
                checked={isOn}
                onChange={handleAutoRenewToggle(sub)}
                disabled={togglingAutoRenew[sub.id] || !canToggle}
                onClick={(e) => e.stopPropagation()}
                sx={{
                  opacity: !canToggle ? 0.5 : 1,
                  '& .MuiSwitch-switchBase': {
                    color: '#fff',
                    '&.Mui-checked': {
                      color: '#fff',
                    },
                    '&.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#34C759',
                      opacity: 1,
                    },
                  },
                  '& .MuiSwitch-track': {
                    backgroundColor: '#FF3B30',
                    opacity: 1,
                  },
                }}
              />
            )
          },
          csv: true,
          csvFill: (sub: SubscriptionSimple) => sub.autoRenew === true ? 'On' : 'Off',
          sortValueGetter: (s) => s.autoRenew === true ? 'on' : 'off',
        },
        ...(isEmployeeBenefit ? [{
          label: '',
          cellFill: (sub: SubscriptionSimple) => (
            <IconButton size="small" onClick={handleActionClick(sub)}>
              <MoreVertical size={16} />
            </IconButton>
          ),
          csv: false,
          sortable: false,
          hide: false,
        }] : []),
        ]}
        handleRowClicked={(row: SubscriptionSimple) => { }}
        id={'membership-subscriptions-table'}
      ></Table>
      <Menu
        anchorReference="anchorPosition"
        anchorPosition={actionAnchorPos || undefined}
        open={Boolean(actionAnchorPos)}
        onClose={closeMenu}
        keepMounted
      >
        <MenuItem onClick={openRemoveDialog}>Remove Employee</MenuItem>
      </Menu>
      <Dialog open={addModalOpen} onClose={closeAddModal} fullWidth maxWidth="sm">
        <form onSubmit={handleAddEmployee}>
          <DialogTitle>Add Employee Membership</DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" style={{ gap: 12 }}>
              <TextField
                label="First name"
                value={employeeForm.firstName}
                onChange={handleEmployeeFieldChange('firstName')}
                fullWidth
                required
              />
              <TextField
                label="Last name"
                value={employeeForm.lastName}
                onChange={handleEmployeeFieldChange('lastName')}
                fullWidth
                required
              />
              <TextField
                label="Phone number"
                type="tel"
                value={employeeForm.phone}
                onChange={handleEmployeeFieldChange('phone')}
                fullWidth
                inputProps={{
                  pattern: '[0-9]{10}',
                  title: 'Phone number must be 10 digits (e.g., 8023451540)'
                }}
                helperText="10 digits, no spaces or dashes"
              />
              <TextField
                label="Email"
                type="email"
                value={employeeForm.email}
                onChange={handleEmployeeFieldChange('email')}
                fullWidth
                required
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeAddModal} color="secondary" disabled={employeeSubmitting}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" color="primary" disabled={employeeSubmitting}>
              {employeeSubmitting ? 'Adding...' : 'Add'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      <Dialog open={confirmOpen} onClose={closeRemoveDialog}>
        <DialogTitle>Remove employee?</DialogTitle>
        <DialogContent>
          {selectedSub && (
            <p>Remove {selectedSub.patron.firstName} {selectedSub.patron.lastName} from this membership?</p>
          )}
          <FormControlLabel
            control={
              <Checkbox
                checked={notifyUser}
                onChange={(e) => setNotifyUser(e.target.checked)}
                color="primary"
              />
            }
            label="Notify the employee"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRemoveDialog} disabled={removing}>
            Cancel
          </Button>
          <Button onClick={handleRemove} color="secondary" variant="contained" disabled={removing}>
            {removing ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </div >
  )
}

const useStyles = makeStyles({
  loadingContainer: {
    width: '100%',
    height: 300,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  container: {
    width: '100%',
    maxHeight: '100%',
    display: 'flex',
    overflow: 'hidden',
    flex: '1 1 auto',
    boxSizing: 'border-box',
    flexDirection: 'column',
    color: colors.STITCH_BLACK,
    padding: 24,
  },
})

export default MembershipSubscriptionsTable
